/**
 * useBackdatingLimit Hook
 *
 * Calculates the minimum allowed date for backdating based on the current user's role.
 * 100% local — no API call, uses shared constants and the role from auth context.
 *
 * @see Sprint 34 - Antidatage + droits
 */

import { useMemo } from 'react';
import { addDays, subDays, startOfDay, endOfDay } from 'date-fns';
import { BACKDATING_LIMITS_DAYS } from '@bstock/shared';
import { useCurrentRole } from './usePermission';

/**
 * Returns the symmetric date range allowed for the current user's role.
 *
 * @returns { minDate, maxDate, limitDays }
 * - minDate / maxDate : Date | undefined — undefined = no limit (ADMIN/OWNER)
 * - limitDays         : raw limit in days (0, 2, Infinity)
 */
export function useBackdatingLimit() {
  const role = useCurrentRole();

  return useMemo(() => {
    const limitDays = role ? (BACKDATING_LIMITS_DAYS[role] ?? 0) : 0;

    if (limitDays === Infinity) {
      return { minDate: undefined, maxDate: undefined, limitDays };
    }

    const today = startOfDay(new Date());
    return {
      minDate: subDays(today, limitDays),
      maxDate: endOfDay(addDays(today, limitDays)),
      limitDays,
    };
  }, [role]);
}
